import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { SUGGESTIONS_TAG_TYPE } from './constants';

// Types for suggestion items
export interface SuggestionItem {
  id: string | number;
  name: string;
}

export interface CrmOwnerSuggestion {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
}

export interface CrmStatusSuggestion {
  crm_type: string;
  crm_status: string;
}

export interface TagSuggestion {
  id: number;
  name: string;
}

export interface OpenPaymentsCompany {
  id: number;
  name: string;
}

export interface SiteOfCareSubtype {
  id: string;
  name: string;
}

export interface SiteOfCareType {
  id: string;
  name: string;
  subtypes: SiteOfCareSubtype[];
}

export interface SiteOfCareAttribute {
  label: string;
  value: string;
}

// Paginated response type
export interface PaginatedSuggestionResponse<T> {
  results: T[];
  next: string | null;
  previous: string | null;
  count: number;
}

// Query argument types
interface SuggestionsQueryArg {
  q: string;
}

interface PayerTypeSuggestionsQueryArg {
  q: string;
  payer_mix?: number;
}

type EducationProgramType = 'residency' | 'fellowship' | 'medical_school';

interface EducationSuggestionsQueryArg {
  q: string;
  program_type?: EducationProgramType;
}

export const suggestionsApi = createApi({
  reducerPath: 'suggestions',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [SUGGESTIONS_TAG_TYPE],
  endpoints: (builder) => ({
    getSpecialties: builder.infiniteQuery<
      PaginatedSuggestionResponse<SuggestionItem>,
      SuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/entities/specialties/`,
          params: { q: queryArg.q, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    // CRM Statuses returns an array directly (not paginated)
    getCrmStatusesSuggestions: builder.query<
      CrmStatusSuggestion[],
      SuggestionsQueryArg
    >({
      query: ({ q }) => ({
        url: `v1/integrations/crm-links/distinct-statuses/`,
        params: { q },
      }),
      providesTags: (result) => {
        if (!result) return [];
        return result
          .filter((item): item is CrmStatusSuggestion => !!item?.crm_type)
          .map(({ crm_type, crm_status }) => ({
            type: SUGGESTIONS_TAG_TYPE,
            id: `${crm_type}::${crm_status}`,
          }));
      },
    }),

    getProviderLists: builder.infiniteQuery<
      PaginatedSuggestionResponse<SuggestionItem>,
      SuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/provider-lists/lists/`,
          params: { q: queryArg.q, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    getPlaceOfServiceSuggestions: builder.infiniteQuery<
      PaginatedSuggestionResponse<SuggestionItem>,
      SuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/entities/places-of-service/`,
          params: { q: queryArg.q, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    getPayerNameSuggestions: builder.infiniteQuery<
      PaginatedSuggestionResponse<SuggestionItem>,
      SuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/entities/payer-names/`,
          params: { q: queryArg.q, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    getPayerTypeSuggestions: builder.infiniteQuery<
      PaginatedSuggestionResponse<SuggestionItem>,
      PayerTypeSuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/entities/payer-types/`,
          params: { q: queryArg.q, payer_mix: queryArg.payer_mix, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    getSiteOfCareTypes: builder.query<SiteOfCareType[], void>({
      query: () => ({
        url: `v1/entities/site-of-care-types/`,
        // Taxonomy is small (~11 entries today). Ask for a large page so we
        // never paginate; if the BE ever exceeds this, we'll need to revisit.
        params: { pageSize: 1000 },
      }),
      transformResponse: (
        response: PaginatedSuggestionResponse<SiteOfCareType>
      ) => response.results,
      providesTags: (result) => {
        if (!result) return [];
        return result.flatMap((type) => [
          { type: SUGGESTIONS_TAG_TYPE, id: type.id },
          ...type.subtypes.map((subtype) => ({
            type: SUGGESTIONS_TAG_TYPE,
            id: subtype.id,
          })),
        ]);
      },
    }),

    getSiteOfCareAttributes: builder.query<SiteOfCareAttribute[], void>({
      query: () => ({
        url: 'v1/entities/site-of-care/attributes/',
        params: { pageSize: 1000 },
      }),
      transformResponse: (
        response: PaginatedSuggestionResponse<SiteOfCareAttribute>
      ) => response.results,
    }),

    getOpenPaymentsSuggestions: builder.infiniteQuery<
      PaginatedSuggestionResponse<OpenPaymentsCompany>,
      SuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/entities/open-payments-companies/`,
          params: { q: queryArg.q, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    getOpenPaymentsCategorySuggestions: builder.infiniteQuery<
      PaginatedSuggestionResponse<SuggestionItem>,
      SuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/entities/open-payments/payment-types/`,
          params: { q: queryArg.q, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    // Keep as regular query - single item lookup by ID
    getOpenPaymentsCompanyById: builder.query<OpenPaymentsCompany, { id: string }>(
      {
        query: ({ id }) => ({
          url: `v1/entities/open-payments-companies/${id}/`,
        }),
        providesTags: (result, error, { id }) => [
          { type: SUGGESTIONS_TAG_TYPE, id },
        ],
      }
    ),

    getHospitalSystemsSuggestions: builder.infiniteQuery<
      PaginatedSuggestionResponse<SuggestionItem>,
      SuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/entities/systems/`,
          params: { q: queryArg.q, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    getGPOAffiliationsSuggestions: builder.infiniteQuery<
      PaginatedSuggestionResponse<SuggestionItem>,
      SuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/entities/gpos/`,
          params: { q: queryArg.q, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    getEducationSuggestions: builder.infiniteQuery<
      PaginatedSuggestionResponse<SuggestionItem>,
      EducationSuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/entities/educational-institutions/`,
          params: {
            q: queryArg.q,
            program_type: queryArg.program_type,
            page: pageParam,
          },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    getTagSuggestions: builder.infiniteQuery<
      PaginatedSuggestionResponse<TagSuggestion>,
      SuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/accounts/tags/`,
          params: { q: queryArg.q, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),

    getCrmOwnerSuggestions: builder.infiniteQuery<
      PaginatedSuggestionResponse<CrmOwnerSuggestion>,
      SuggestionsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        return {
          url: `v1/integrations/crm-links/owners/`,
          params: { q: queryArg.q, page: pageParam },
        };
      },
      providesTags: (result) => {
        const items = result?.pages?.flatMap((p) => p.results) ?? [];
        return items.map(({ id }) => ({ type: SUGGESTIONS_TAG_TYPE, id }));
      },
    }),
  }),
});

export const {
  useGetSpecialtiesInfiniteQuery,
  useGetCrmStatusesSuggestionsQuery,
  useGetProviderListsInfiniteQuery,
  useGetPlaceOfServiceSuggestionsInfiniteQuery,
  useGetPayerNameSuggestionsInfiniteQuery,
  useGetOpenPaymentsSuggestionsInfiniteQuery,
  useGetOpenPaymentsCategorySuggestionsInfiniteQuery,
  useGetOpenPaymentsCompanyByIdQuery,
  useGetHospitalSystemsSuggestionsInfiniteQuery,
  useGetGPOAffiliationsSuggestionsInfiniteQuery,
  useGetEducationSuggestionsInfiniteQuery,
  useGetPayerTypeSuggestionsInfiniteQuery,
  useGetSiteOfCareTypesQuery,
  useGetSiteOfCareAttributesQuery,
  useGetTagSuggestionsInfiniteQuery,
  useGetCrmOwnerSuggestionsInfiniteQuery,
} = suggestionsApi;
